import {
  Container,
  Box,
  styled,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  useTheme,
  CssBaseline,
  Drawer,
  IconButton,
  Divider,
} from "@mui/material";
import Header from "@/components/Header";
import { Outlet, useParams } from "react-router-dom";
import DocumentViewerDrawer from "@/components/review/DocumentViewerDrawer";
import { Loan, useDealContext } from "@/contexts/DealContext";
import { useEffect, useState } from "react";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import { Close } from "@mui/icons-material";
import { isInIframe } from "@/services/iframe.service";

const drawerWidth = 500;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const Main = styled("main", { shouldForwardProp: (prop) => prop !== "open" })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  backgroundColor: "white",
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: open ? `-${drawerWidth}px` : 0,
  variants: [
    {
      props: { open: true },
      style: {
        transition: theme.transitions.create("margin", {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginRight: 0,
        padding: 0,
      },
    },
  ],
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        width: `calc(100% - ${drawerWidth}px)`,
        marginRight: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const MainLayout = () => {
  const {
    deal: { loans },
    drawerOpen,
    setDrawerOpen: setOpen,
  } = useDealContext();
  const { loanId } = useParams();
  const getLoan = (id: string) => loans.find((loan) => loan.id === id) ?? loans[0];
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(loanId ? getLoan(loanId) : null);

  useEffect(() => {
    if (loanId) {
      const loan = getLoan(loanId);
      setSelectedLoan(loan);
    }
  }, [loanId, loans]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <CssBaseline />
      {!isInIframe() && (
        <AppBar position="fixed" open={drawerOpen}>
          <Header />
        </AppBar>
      )}
      <Main open={drawerOpen}>
        {!isInIframe() && <DrawerHeader />}
        <Container
          component="main"
          style={drawerOpen ? { padding: 0 } : {}}
          sx={{ flexGrow: 1, py: 0, pr: 0, pl: 0, height: "100%" }}
        >
          <Outlet />
        </Container>
      </Main>
      <Drawer
        sx={{
          width: drawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="right"
        open={drawerOpen}
      >
        <DrawerHeader>
          <Box
            style={{ width: "100%" }}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <IconButton onClick={() => setOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DrawerHeader>
        <Divider />
        {(selectedLoan || loans.length > 0) && (
          <DocumentViewerDrawer
            open={drawerOpen}
            onClose={() => setOpen(false)}
            fileUrl={selectedLoan?.documentUrl || loans[0].documentUrl}
            fileType={selectedLoan?.documentType || loans[0].documentType}
          />
        )}
      </Drawer>
      {/* <Footer /> */}
    </Box>
  );
};

export default MainLayout;
